import type { ChartPeriod, ChartDimensions, TimeGranularity } from '../../types';
import { MONTH_NAMES, CHART_CONSTANTS } from '../../constants';
import { getQuarter } from '../../utils';

interface XAxisLabelsProps {
  periods: ChartPeriod[];
  dimensions: ChartDimensions;
  height: number;
  hasComparison: boolean;
  comparisonStartDate?: Date;
  startDate: Date;
  granularity: TimeGranularity;
}

/**
 * Renders X-axis period labels.
 * Shows dual labels when comparing periods.
 */
export function XAxisLabels({
  periods,
  dimensions,
  height,
  hasComparison,
  comparisonStartDate,
  startDate,
  granularity,
}: XAxisLabelsProps) {
  const { getX } = dimensions;
  const { COLORS } = CHART_CONSTANTS;

  const getComparisonLabel = (periodKey: string): string => {
    if (!comparisonStartDate) return '';

    const offsetMs = startDate.getTime() - comparisonStartDate.getTime();

    let periodDate: Date;
    switch (granularity) {
      case 'accumulated':
        return 'Total';
      case 'yearly':
        periodDate = new Date(`${periodKey}-01-01T00:00:00`);
        break;
      case 'quarterly': {
        const [year, q] = periodKey.split('-Q');
        const quarterMonth = (parseInt(q) - 1) * 3;
        periodDate = new Date(`${year}-${String(quarterMonth + 1).padStart(2, '0')}-01T00:00:00`);
        break;
      }
      case 'monthly':
        periodDate = new Date(periodKey + '-01T00:00:00');
        break;
      default:
        periodDate = new Date(periodKey + 'T00:00:00');
    }

    const compDate = new Date(periodDate.getTime() - offsetMs);

    switch (granularity) {
      case 'yearly':
        return String(compDate.getFullYear());
      case 'quarterly':
        return `Q${getQuarter(compDate)}/${String(compDate.getFullYear()).slice(2)}`;
      default:
        return `${MONTH_NAMES[compDate.getMonth()]}/${String(compDate.getFullYear()).slice(2)}`;
    }
  };

  return (
    <>
      {periods.map((p, i) => {
        const x = getX(i);
        return (
          <g key={`label-${p.key}`}>
            {hasComparison && comparisonStartDate ? (
              <>
                {/* Current period label (primary) - top row */}
                <text
                  x={x}
                  y={height - 35}
                  textAnchor="middle"
                  fontSize="11"
                  fill={COLORS.CURRENT}
                  fontWeight="500"
                >
                  {p.label}
                </text>
                {/* Comparison period label (blue) - bottom row */}
                <text
                  x={x}
                  y={height - 18}
                  textAnchor="middle"
                  fontSize="11"
                  fill={COLORS.COMPARISON}
                >
                  {getComparisonLabel(p.key)}
                </text>
              </>
            ) : (
              <text
                x={x}
                y={height - 15}
                textAnchor="middle"
                fontSize="11"
                fill={COLORS.LABEL}
              >
                {p.label}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}
